import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import { CHAT_CONFIG, CHAT_STATUS } from './constants';

/**
 * Internal state
 */
let initializationState = CHAT_STATUS.NOT_INITIALIZED;

/**
 * Check if XenoTalk is available
 */
function isWidgetAvailable() {
  return typeof window.XenoTalk !== 'undefined';
}

/**
 * Get current user from store
 */
function getCurrentUser() {
  const userStore = getStore('user');
  return userStore?.state?.current;
}

/**
 * Helper to build user display name
 */
function getUserDisplayName(user) {
  if (!user) return 'Client';
  return (
    `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
    user.email ||
    'Client'
  );
}

/**
 * Vérifie si de nouveaux messages peuvent être envoyés pour une conversation
 * @param {Object} conversation
 * @returns {Promise<boolean>}
 */
async function checkCanAddNewMessage(conversation) {
  try {
    const meta = conversation?.metadata || {};
    const type = meta.type || meta.orderType;

    // Si ce n'est ni une commande ni un colis (ex: support admin), pas de blocage
    if (type !== 'order' && type !== 'package') {
      return true;
    }

    const id =
      type === 'order' ? meta.orderId || meta.id : meta.packageId || meta.id;
    if (!id) {
      return true;
    }

    const endpoint =
      type === 'order'
        ? `https://api.monrapidoo.com/order/details/${id}?group=ultraxeno`
        : `https://api.monrapidoo.com/package/details/${id}?group=ultraxeno`;

    const res = await fetch(endpoint, {
      method: 'GET'
    });

    if (!res.ok) {
      return false;
    }

    const json = await res.json();
    const status = json?.data?.status;

    // Si livré, pas autorisé à envoyer de nouveaux messages
    if (status === 'delivered') {
      return false;
    }

    return true;
  } catch (error) {
    console.error(
      'Erreur lors de la vérification du statut de la commande/colis:',
      error
    );
    return false;
  }
}

async function initChatWidget() {
  // Skip if already initializing or initialized
  if (
    initializationState === CHAT_STATUS.INITIALIZING ||
    initializationState === CHAT_STATUS.INITIALIZED
  ) {
    return;
  }

  initializationState = CHAT_STATUS.INITIALIZING;
  bus.emit(constants.EVENTS.CHAT.INIT);

  // Check if XenoTalk script is loaded
  if (!isWidgetAvailable()) {
    console.warn('XenoTalk not available - script may not be loaded yet');
    bus.emit(
      constants.EVENTS.CHAT.INIT_ERROR,
      new Error('XenoTalk script not loaded')
    );
    initializationState = CHAT_STATUS.ERROR;
    return;
  }

  // Get current user
  const user = getCurrentUser();
  if (!user || !user.id) {
    console.log('No user logged in, skipping chat initialization');
    initializationState = CHAT_STATUS.NOT_INITIALIZED;
    return;
  }

  try {
    // Initialize the widget with user data and customization options
    window.XenoTalk.init({
      primaryColor: '#ff7200', // Couleur principale (Orange)
      activateSound: true, // Activer les effets sonores
      title: 'XenoTalk Support', // Titre du chat
      userId: user.id,
      userName: getUserDisplayName(user),
      manualPollMessageInterval: 2,
      checkCanAddNewMessage
    });

    // Create the UI
    window.XenoTalk.createUI();
    window.XenoTalk.showChatBubble(); // Afficher la bulle flottante

    window.XenoTalk.on('message:sent', ({ conversation, sender, text }) => {
      const adminId = CHAT_CONFIG.adminUserId;
      const isAdminConv =
        conversation?.participants?.other?.id === adminId ||
        conversation?.participants?.raw?.participant2Id === adminId;

      if (!isAdminConv) return;

      notifyAdmins(text, sender?.name || 'Client', {
        conversationId: conversation.conversationId,
        referenceId: conversation.referenceId,
        referenceType: conversation.referenceType
      });
    });

    initializationState = CHAT_STATUS.INITIALIZED;
    console.log('Chat widget initialized for customer:', user.id);
    bus.emit(constants.EVENTS.CHAT.INIT_SUCCESS, { userId: user.id });
  } catch (error) {
    console.error('Failed to initialize chat widget:', error);
    initializationState = CHAT_STATUS.ERROR;
    bus.emit(constants.EVENTS.CHAT.INIT_ERROR, error);
  }
}

/**
 * Open chat conversation with admin support
 */
async function openChatWithAdmin() {
  // Ensure widget is available
  if (!isWidgetAvailable()) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Chat indisponible',
      body: 'Le service de chat est en cours de chargement. Veuillez réessayer.'
    });
    return;
  }

  // Get current user
  const user = getCurrentUser();
  if (!user || !user.id) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Connexion requise',
      body: 'Vous devez être connecté pour utiliser le chat.'
    });
    return;
  }

  // Ensure widget is initialized
  if (initializationState !== CHAT_STATUS.INITIALIZED) {
    await initChatWidget();
  }

  try {
    console.log('🚀 ~ openChatWithAdmin ~ user:', user);

    const clientName = getUserDisplayName(user);

    // Nouvelle signature XenoTalk SDK
    const conv = await window.XenoTalk.openConversation({
      // Participant 1 (Le client connecté)
      participant1Id: user.id,
      participant1Role: 'client',
      participant1Name: clientName,
      participant1Tag: 'Client',
      participant1NotifService: 'rapidoo',

      // Participant 2 (L'admin support)
      participant2Id: CHAT_CONFIG.adminUserId,
      participant2Role: 'admin',
      participant2Name: 'Support Client',
      participant2Tag: 'Support Client',
      participant2NotifService: 'rapidoo-admin',

      // Type & Références
      type: 'support',
      referenceType: 'admin',
      referenceId: `v3-admin-customer-${user.id}`
    });

    // Set and open conversation
    console.log('📢[controller.js:150]: conv: ', conv);
    window.XenoTalk.setConversation(conv._id, {
      title: 'Support Client',
      tag: 'Support Client',
      conversation: conv
    });
    window.XenoTalk.openUI();

    // bus.emit(constants.EVENTS.CHAT.OPEN_CONVERSATION, {
    //   conversationId: conv._id,
    //   conversation: conv
    // });
  } catch (error) {
    console.error('Error opening chat with admin:', error);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: "Impossible d'ouvrir le chat. Veuillez réessayer."
    });
    bus.emit(constants.EVENTS.CHAT.INIT_ERROR, error);
  }
}

/**
 * Open chat conversation with rider regarding an order
 * @param {Object} order
 */
async function openChatWithRider(order) {
  console.log(
    '🔍 ~ openChatWithRider ~ v3/rapidoo-customer-v3/src/app/features/chat/controller.js:175 ~ order:',
    order
  );
  // Ensure widget is available
  if (!isWidgetAvailable()) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Chat indisponible',
      body: 'Le service de chat est en cours de chargement.'
    });
    return;
  }

  // Get current user
  const user = getCurrentUser();
  if (!user || !user.id) {
    console.log('User not logged in, cannot open rider chat');
    return;
  }

  // Ensure widget is initialized
  if (initializationState !== CHAT_STATUS.INITIALIZED) {
    await initChatWidget();
  }

  const orderId = order?.id || order?.orderId;
  if (!orderId) {
    console.error('No order ID provided for rider chat');
    return;
  }

  try {
    const clientName = getUserDisplayName(user);
    const riderId = order?.riderId || order?.driverId || 'rider_001';
    const riderName = order?.riderName || order?.driverName || 'Livreur';

    const isPackage = Boolean(order?.packageInfo);
    const itemType = isPackage ? 'package' : 'order';
    const packageId =
      order?.packageInfo?.id || order?.packageInfo?._id || orderId;

    // Nouvelle signature XenoTalk SDK
    const conv = await window.XenoTalk.openConversation({
      // Participant 1 (Le client)
      participant1Id: user.id,
      participant1Role: 'client',
      participant1Name: clientName,
      participant1Tag: 'Client',
      participant1NotifService: 'rapidoo',

      // Participant 2 (Le livreur associé à la commande)
      participant2Id: riderId,
      participant2Role: 'rider',
      participant2Name: riderName,
      participant2Tag: 'Livreur',
      participant2NotifService: 'rapidoo-rider',

      // Type & Références
      type: 'delivery',
      referenceType: isPackage ? 'package' : 'order',
      referenceId: `v3-rider-customer-${orderId}`,

      // Métadonnées pour vérification per-conversation
      metadata: {
        type: itemType,
        orderId: isPackage ? null : orderId,
        packageId: isPackage ? packageId : null
      }
    });

    // Set and open conversation
    window.XenoTalk.setConversation(conv._id, {
      title: riderName,
      tag: 'Livreur',
      conversation: conv
    });
    window.XenoTalk.openUI();

    bus.emit(constants.EVENTS.CHAT.OPEN_CONVERSATION, {
      conversationId: conv._id,
      conversation: conv
    });
  } catch (error) {
    console.error('Error opening chat with rider:', error);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur',
      body: 'Impossible de contacter le livreur.'
    });
  }
}

/**
 * Toggle chat UI (open if closed, close if open)
 */
function toggleChat() {
  if (!isWidgetAvailable()) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Chat indisponible',
      body: "Le service de chat n'est pas disponible."
    });
    return;
  }

  const user = getCurrentUser();
  if (!user || !user.id) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Connexion requise',
      body: 'Vous devez être connecté pour utiliser le chat.'
    });
    return;
  }

  try {
    if (typeof window.XenoTalk.toggle === 'function') {
      window.XenoTalk.toggle();
    } else {
      openChatWithAdmin();
    }
    bus.emit(constants.EVENTS.CHAT.TOGGLE);
  } catch (error) {
    console.error('Error toggling chat:', error);
    openChatWithAdmin();
  }
}

/**
 * Close chat UI
 */
function closeChat() {
  if (isWidgetAvailable() && typeof window.XenoTalk.closeUI === 'function') {
    window.XenoTalk.closeUI();
    bus.emit(constants.EVENTS.CHAT.CLOSE);
  }
}

// ============ EVENT LISTENERS REGISTRATION ============

// Initialize chat on app ready if user is logged in
bus.on(constants.EVENTS.APP.READY, () => {
  const user = getCurrentUser();
  if (user?.id) {
    setTimeout(() => {
      initChatWidget();
    }, 500);
  }
});

// Initialize after successful login
bus.on(constants.EVENTS.USER.LOGIN_SUCCESS, () => {
  setTimeout(() => {
    initChatWidget();
  }, 500);
});

// Initialize after successful registration
bus.on(constants.EVENTS.USER.REGISTER_SUCCESS, () => {
  setTimeout(() => {
    initChatWidget();
  }, 500);
});

// Handle open chat with admin request
bus.on(constants.EVENTS.CHAT.OPEN_WITH_ADMIN, openChatWithAdmin);

// Handle open chat with rider request
bus.on('chat.open_with_rider', openChatWithRider);

// Handle toggle chat request
bus.on(constants.EVENTS.CHAT.TOGGLE, toggleChat);

// Handle close chat request
bus.on(constants.EVENTS.CHAT.CLOSE, closeChat);

async function notifyAdmins(message, userName = 'Client') {
  try {
    await fetch(
      `https://api.monrapidoo.com/notification/send/bulk?group=ultraxeno`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: `Message de ${userName}`,
          content: message,
          service: 'rapidoo-dashboard'
        })
      }
    );
  } catch (error) {
    console.error('Erreur notifyAdmins:', error);
  }
}